import { useEffect, useState } from "react";
import { CheckCircle2, MailCheck, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";

export const EmailSettings = () => {
  const [info, setInfo] = useState(null);
  const [key, setKey] = useState("");
  const [sender, setSender] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/email-settings");
    setInfo(data);
    setSender(data.sender_email);
  };

  useEffect(() => {
    load();
  }, []);

  if (!info) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { sender_email: sender };
      if (key.trim()) payload.resend_api_key = key.trim();
      const { data } = await api.put("/admin/email-settings", payload);
      setInfo(data);
      setKey("");
      toast.success("Saved. Booking alerts will use these settings.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Could not save."));
    } finally {
      setBusy(false);
    }
  };

  const removeKey = async () => {
    try {
      const { data } = await api.delete("/admin/email-settings/key");
      setInfo(data);
      toast.success("Key removed. Booking alerts are now off.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Could not remove the key."));
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/email-settings/test");
      toast.success(`Test email sent to ${data.to}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Test email failed."));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-email-settings">
      <div
        className={`rounded-lg border p-4 flex items-center gap-3 text-sm ${
          info.configured
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : "border-amber-200 bg-amber-50 text-amber-800"
        }`}
        data-testid="admin-email-status"
      >
        {info.configured ? <CheckCircle2 className="w-4 h-4" /> : <MailCheck className="w-4 h-4" />}
        {info.configured
          ? `Booking alerts are ON. Emails go to the patient and to ${info.clinic_notify_email}.`
          : "Booking alerts are OFF. Add a Resend API key below to switch them on. Bookings still work without it."}
      </div>

      <form onSubmit={save} className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-6">
        <div>
          <h2 className="text-lg font-medium text-slate-900">Email alerts (Resend)</h2>
          <p className="text-sm text-slate-500 mt-1">
            Every new booking sends a confirmation to the patient and a notification to{" "}
            <span className="text-slate-700">{info.clinic_notify_email}</span>. Get a free API key from
            resend.com → API Keys.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="resend-key">Resend API key</Label>
          <Input
            id="resend-key"
            type="password"
            value={key}
            data-testid="admin-resend-key-input"
            onChange={(e) => setKey(e.target.value)}
            placeholder={info.masked_key || "re_xxxxxxxxxxxxxxxx"}
            className="rounded-md h-11 font-mono"
          />
          <p className="text-xs text-slate-400">
            {info.configured
              ? `Current key: ${info.masked_key}. Leave blank to keep it, or paste a new key to replace it.`
              : "Starts with re_. Stored securely and never shown again in full."}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sender-email">Send emails from</Label>
          <Input
            id="sender-email"
            type="email"
            required
            value={sender}
            data-testid="admin-sender-email-input"
            onChange={(e) => setSender(e.target.value)}
            className="rounded-md h-11"
          />
          <p className="text-xs text-slate-400">
            This domain must be verified in Resend, otherwise emails will bounce.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            type="submit"
            disabled={busy}
            data-testid="admin-email-save-btn"
            className="h-11 rounded-md bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {busy ? "Saving…" : "Save Changes"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 rounded-md"
            onClick={sendTest}
            disabled={testing || !info.configured}
            data-testid="admin-email-test-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {testing ? "Sending…" : "Send test email"}
          </Button>
          {info.configured && (
            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-md text-red-600 hover:bg-red-50"
              onClick={removeKey}
              data-testid="admin-email-remove-key-btn"
            >
              Remove key
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};
